import type { BackOfficeAccess } from "@/features/auth/back-office-access";

/**
 * The platform back-office, in four subjects.
 *
 * It used to be eleven tabs in one row, which is a list rather than a
 * structure: nothing said that "Stabilité" and "Appel de test" answer the same
 * question, or that "Demandes de numéro" and "Accès clients" are both things
 * you do *to a client*. Eleven equal choices also means the first one is the
 * one people click, and the eleventh may as well not exist.
 *
 * Two levels instead: the subject, then its pages. A subject is "what am I
 * here for" — a client to sort out, what happened today, whether the service is
 * healthy, what the platform costs — and no subject has more than five pages.
 *
 * These subjects are now rendered by the back-office's own sidebar
 * (`views/admin/sidebar.tsx`) rather than by a row of tabs: once the
 * back-office became an application of its own, a left rail could show every
 * subject at once and the two-row tab bar was showing the same thing twice.
 * This file stays the single declaration of what those subjects are — it is
 * imported, not duplicated.
 */
/**
 * The control centre stands above the subjects rather than inside one: it is the
 * page that says which subject needs you, so it is the first thing in the rail.
 */
export const ADMIN_HOME = { to: "live", label: "Centre de contrôle" } as const;

type AdminPage = {
  to: string;
  label: string;
  /**
   * Also shown to the read-only account (`SYSADMIN_READ`, sales): a page that
   * only reads and is not technical. The server is what lets that account in or
   * not (`checkStaffRead`); this only keeps the menu from offering a 403.
   */
  read?: boolean;
};

export const ADMIN_SECTIONS: {
  key: string;
  label: string;
  pages: AdminPage[];
}[] = [
  {
    key: "clients",
    label: "Clients",
    pages: [
      { to: "clients", label: "Clients", read: true },
      { to: "number-requests", label: "Demandes de numéro" },
      { to: "provisioning", label: "Créer un accès" },
      { to: "demo-spaces", label: "Espaces de démo" },
      { to: "telephony", label: "Téléphonie" },
    ],
  },
  {
    key: "activity",
    label: "Activité",
    pages: [
      { to: "dashboard", label: "Vue globale", read: true },
      { to: "sessions", label: "Sessions" },
    ],
  },
  {
    key: "quality",
    label: "Qualité de service",
    pages: [
      { to: "technical", label: "Analyse technique" },
      { to: "stability", label: "Stabilité" },
      { to: "synthetic-calls", label: "Appel de test" },
      { to: "media-health", label: "Audio des appels" },
      { to: "external-calls", label: "APIs externes" },
      { to: "tool-errors", label: "Outils en erreur" },
    ],
  },
  {
    key: "platform",
    label: "Plateforme",
    pages: [
      { to: "costs", label: "Coûts" },
      { to: "credits", label: "Crédits" },
      { to: "context-pressure", label: "Fenêtre de contexte" },
    ],
  },
];

/**
 * The subjects as one account sees them: everything for a SYSADMIN; for the
 * read-only account, the pages marked `read` — and a subject left with none is
 * not drawn at all.
 */
export const adminSectionsFor = (access: BackOfficeAccess) =>
  access === "admin"
    ? ADMIN_SECTIONS
    : ADMIN_SECTIONS.map((section) => ({
        ...section,
        pages: section.pages.filter((page) => page.read),
      })).filter((section) => section.pages.length > 0);
